<script setup lang="ts">
import type { fetchedUser } from "~/types/fetchedUser"
import { ref, computed, onBeforeMount, nextTick } from 'vue'
import { getAllUsers } from '@/composables/userComposables'
import DefaultProfileImageDisplayer from '@/components/reusable/defaultProfileImageDisplayer.vue'

const props = defineProps<{
    showArrow?: boolean
    position?: 'top' | 'bottom' | 'left' | 'right'
    width?: string
    excludeUsers?: string[] // Array of user IDs to exclude
}>()

const emits = defineEmits<{
    userSelected: [user: fetchedUser]
    cancelled: []
}>()

const usersContainer = ref<HTMLElement | null>(null)
const users = ref<fetchedUser[]>([])
const isLoadingUsers = ref<boolean>(false)

// Create a computed property which maps each fetchedUser to a UserPickerUser
const mappedUsers = computed(() => {
    return users.value
        .filter(user => !props.excludeUsers?.includes(user.id))
        .map(user => ({
            id: user.id,
            emailAddress: user.emailAddress || '',
            profileImage: user.profileImage || '',
            userName: user.userName || ''
        }))
})

function selectUser(user: fetchedUser) {
    emits('userSelected', user)
}

function handleBlur() {
    emits('cancelled')
}

onBeforeMount(() => {
    getAllUsers(users.value, isLoadingUsers)
    nextTick(() => {
        usersContainer.value?.focus()
    })
})
</script>

<template>
    <div 
        ref="usersContainer" 
        tabindex="0" 
        @blur="handleBlur"
        class="users-container"
        :class="{
            'show-arrow': showArrow !== false,
            [`position-${position || 'bottom'}`]: true
        }"
        :style="{ width: width || '22rem' }"
    >
        <div v-if="isLoadingUsers" class="loading-state">
            <p>Loading users...</p>
        </div>
        <div 
            v-else
            @click="selectUser(user)" 
            v-for="user in mappedUsers" 
            :key="user.id" 
            class="user"
        >
            <img v-if="user.profileImage" :src="user.profileImage" referrerpolicy="no-referrer" class="rounded-image-mini">
            <default-profile-image-displayer width="2.4rem" v-else :firstCharacter="user.userName?.charAt(0) || 'U'"></default-profile-image-displayer>
            <p>{{ user.userName }}</p>
        </div>
        <div v-if="!isLoadingUsers && mappedUsers.length === 0" class="no-users">
            <p>No users available</p>
        </div>
    </div>
</template>

<style scoped>
.users-container {
    position: absolute;
    border: 1px solid var(--accent-gray-subtle);
    display: flex;
    flex-direction: column;
    box-shadow: var(--main-box-shadow);
    border-radius: 1rem;
    padding: 2rem;
    background-color: var(--box-color);
    z-index: 10;
    outline: none;
}

/* Arrow positioning */
.users-container.show-arrow::before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
}

.users-container.position-bottom.show-arrow::before {
    top: -1rem;
    right: 50%;
    border-left: 1rem solid transparent;
    border-right: 1rem solid transparent;
    border-bottom: 1rem solid white;
}

.users-container.position-top.show-arrow::before {
    bottom: -1rem;
    right: 50%;
    border-left: 1rem solid transparent;
    border-right: 1rem solid transparent;
    border-top: 1rem solid white;
}

.users-container.position-left.show-arrow::before {
    right: -1rem;
    top: 50%;
    border-top: 1rem solid transparent;
    border-bottom: 1rem solid transparent;
    border-left: 1rem solid white;
}

.users-container.position-right.show-arrow::before {
    left: -1rem;
    top: 50%;
    border-top: 1rem solid transparent;
    border-bottom: 1rem solid transparent;
    border-right: 1rem solid white;
}

/* Default positioning */
.users-container.position-bottom {
    top: 3rem;
    right: -12rem;
}

.users-container.position-top {
    bottom: 3rem;
    right: -12rem;
}

.users-container.position-left {
    right: 3rem;
    top: -50%;
}

.users-container.position-right {
    left: 3rem;
    top: -50%;
}

.user {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    transition: all .3s;
    font-size: 1.4rem;
    margin-bottom: 1rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
}

.user:hover {
    background-color: var(--accent-gray-dark);
}

.user:last-child {
    margin-bottom: 0;
}

.loading-state, .no-users {
    padding: 1rem;
    text-align: center;
    color: var(--accent-gray);
    font-style: italic;
}
</style>
